import { FC, useState } from 'react';
import {
  Box,
  Center,
  Circle,
  Divider,
  HStack,
  Image,
  Text,
  VStack,
} from '@chakra-ui/react';

import clipboardImg from 'assets/brand/clipboard@2x.png';
import Button from 'components/_core/Button';
import BackButton from 'components/BackButton';
import { StepProps } from 'components/Stepper/types';
import Upload, { FILE_TYPE, UploadMessage } from 'components/Upload';
import { UploadedFileInfo } from 'components/Upload/types';
import TrackView from 'features/Analytics/TrackView';
import {
  BankTransferCreationStep,
  useBankTransferContext,
} from 'features/Payments/Pages/Transfers/contexts';
import { uploadReceipt } from 'features/Storage/upload';
import * as ContentLayout from 'layouts/ContentLayout';
import * as Layout from 'layouts/StepperLayout';
import spacing from 'theme/foundations/space';

import DocumentReceipt from './components/DocumentReceipt';
import locales from './locales';

const AddReceipt: FC<StepProps<BankTransferCreationStep>> = (props) => {
  const { setBankTransfer } = useBankTransferContext();
  const [uploadFileInfo, setUploadFileInfo] = useState<
    UploadedFileInfo | undefined
  >(undefined);

  const onReceiptSuccess = (data: UploadedFileInfo) => {
    setUploadFileInfo(data);
  };

  const onDocumentDelete = () => {
    setUploadFileInfo(undefined);
  };

  const onSubmitReceipt = () => {
    if (!uploadFileInfo) {
      return;
    }
    setBankTransfer((prevState) => ({
      ...prevState,
      receipt: {
        bucket: uploadFileInfo.bucketName,
        fileRef: uploadFileInfo.fileRef,
        url: uploadFileInfo.url,
      },
    }));
    props.goNext();
  };

  return (
    <Layout.Root>
      <Layout.Body>
        <TrackView pageName="NewTransferAddReceipt" />

        <Layout.HeaderButtonsContainer>
          <BackButton onClick={props.goBack} />
        </Layout.HeaderButtonsContainer>

        <Layout.BodyScrollableSection>
          <Box marginBottom="space-24">
            <ContentLayout.Header
              paddingBottom="space-8"
              title={locales.title}
            />
            <Text>{locales.description}</Text>
          </Box>

          {uploadFileInfo ? (
            <VStack
              alignItems="flex-start"
              marginBottom="space-40"
              spacing="space-8"
            >
              <Text marginBottom={spacing['space-8']} variant="light">
                {locales.receipt}
              </Text>
              <DocumentReceipt
                documentUrl={uploadFileInfo.url}
                onDocumentDelete={onDocumentDelete}
              />
            </VStack>
          ) : (
            <Box
              backgroundColor="grey.200"
              borderRadius="radius-16"
              marginBottom="space-40"
            >
              <Upload
                backgroundColor="inherit"
                dashedBorderColor="grey.600"
                errorMessage="error"
                fileTypes={FILE_TYPE.ALLOWED_DOCUMENTS}
                onSuccessfulUpload={onReceiptSuccess}
                uploadFile={uploadReceipt}
                withLoader
              >
                <Center padding="space-16">
                  <UploadMessage
                    dragAndDropMessage={locales.dropMessage}
                    imageElement={<Image boxSize="120px" src={clipboardImg} />}
                  />
                </Center>
              </Upload>
            </Box>
          )}

          <VStack alignItems="flex-start" spacing="space-24">
            <Text>{locales.typeOfDocumentsAccepted}</Text>
            <VStack alignItems="flex-start" spacing="space-16">
              <HStack>
                <Circle bgColor="grey.200" size={20}>
                  <Text fontWeight="weight-500" size="sm" variant="primary">
                    1
                  </Text>
                </Circle>
                <Text fontWeight="weight-400" variant="primary">
                  {locales.documentRib}
                </Text>
              </HStack>
              <HStack>
                <Circle bgColor="grey.200" size={20}>
                  <Text fontWeight="weight-500" size="sm" variant="primary">
                    2
                  </Text>
                </Circle>
                <Text fontWeight="weight-400" variant="primary">
                  {locales.documentBill}
                </Text>
              </HStack>
            </VStack>
          </VStack>
        </Layout.BodyScrollableSection>

        <Layout.FooterContainer>
          <Divider />
          <Layout.FooterButtonsContainer>
            <Button
              icon="arrow-right"
              iconPosition="right"
              isDisabled={!uploadFileInfo}
              onClick={onSubmitReceipt}
              type="submit"
            >
              {locales.next}
            </Button>
          </Layout.FooterButtonsContainer>
        </Layout.FooterContainer>
      </Layout.Body>
    </Layout.Root>
  );
};

export default AddReceipt;
